/**
 * findingsStore.ts — M6 Output Factory · Increment 2 (2026-06-25, Anil).
 *
 * The impure Firestore seam for the FINDINGS POOL (pure shape in findingsModel.ts).
 * Findings live at `clients/{cid}/findings/{findingId}` — team-read/create/update
 * (firestore.rules Inc-2 additive matcher; no hard delete). One canonical doc per
 * (project, findingType) using `findingIdFor`; the report (Inc 3) draws only from
 * `status === 'confirmed'` findings.
 *
 * The pure builders/transitions are in findingsModel.ts; this file only touches
 * Firestore. A confirm transition is the AUO confirm-loop applied to every
 * finding type — staff confirm flips `status → confirmed` + stamps confirmedBy.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  blankFinding,
  findingIdFor,
  type Finding,
  type FindingSource,
  type FindingStatus,
  type FindingType,
} from './findingsModel';

/** Map a Firestore doc → Finding (defensive). */
function fromDoc(id: string, d: Record<string, unknown>): Finding {
  return {
    id,
    projectId: (d.projectId as string) || '',
    type: d.type as FindingType,
    source: (d.source as FindingSource) || 'manual',
    status: (d.status as FindingStatus) || 'to_assess',
    payload: (d.payload as Record<string, unknown>) || {},
    ref: (d.ref as Finding['ref']) ?? null,
    confirmedBy: (d.confirmedBy as string) ?? null,
    confirmedAt: typeof d.confirmedAt === 'string' ? d.confirmedAt : null,
    createdAt: typeof d.createdAt === 'string' ? d.createdAt : undefined,
    updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
    updatedBy: (d.updatedBy as string) ?? null,
  };
}

/**
 * Stream a project's findings (team-read). Filtered by `projectId` so a client
 * with several projects keeps separate pools. Requires no composite index (single
 * equality filter).
 */
export function listenProjectFindings(
  clientId: string,
  projectId: string,
  cb: (findings: Finding[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  if (!clientId || !projectId) { cb([]); return () => {}; }
  const col = collection(db, 'clients', clientId, 'findings');
  const q = query(col, where('projectId', '==', projectId));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((s) => fromDoc(s.id, s.data() as Record<string, unknown>))),
    (e) => {
      // self-heal pattern (AD-026): surface the error, do not silent-empty.
      console.error('[findingsStore] listener error', e);
      onErr?.(e);
    },
  );
}

/**
 * Upsert a finding (create-or-update the canonical one-per-type doc). Used to
 * seed a `to_assess` finding, attach a payload/ref, or move it to `ai_drafted`.
 */
export async function upsertFinding(
  clientId: string,
  projectId: string,
  type: FindingType,
  patch: Partial<Pick<Finding, 'source' | 'status' | 'payload' | 'ref'>>,
  actorUid: string,
): Promise<string> {
  const id = findingIdFor(projectId, type);
  const base = blankFinding(projectId, type);
  await setDoc(
    doc(db, 'clients', clientId, 'findings', id),
    {
      projectId,
      type,
      source: patch.source ?? base.source,
      status: patch.status ?? base.status,
      ...(patch.payload !== undefined ? { payload: patch.payload } : {}),
      ...(patch.ref !== undefined ? { ref: patch.ref } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    },
    { merge: true },
  );
  return id;
}

/**
 * The CONFIRM-LOOP transition — staff confirm a finding (any type) → POOL-READY.
 * Stamps `confirmedBy`/`confirmedAt`. Nothing reaches a report un-confirmed.
 */
export async function confirmFinding(
  clientId: string,
  projectId: string,
  type: FindingType,
  actorUid: string,
): Promise<void> {
  const id = findingIdFor(projectId, type);
  // setDoc(merge) so confirming a not-yet-seeded finding still creates it confirmed.
  await setDoc(
    doc(db, 'clients', clientId, 'findings', id),
    {
      projectId,
      type,
      status: 'confirmed' as FindingStatus,
      confirmedBy: actorUid,
      confirmedAt: new Date().toISOString(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    },
    { merge: true },
  );
}

/** Move a confirmed/ai-drafted finding back to `to_assess` (un-confirm / re-open). */
export async function reopenFinding(
  clientId: string,
  projectId: string,
  type: FindingType,
  actorUid: string,
): Promise<void> {
  const id = findingIdFor(projectId, type);
  await updateDoc(doc(db, 'clients', clientId, 'findings', id), {
    status: 'to_assess' as FindingStatus,
    confirmedBy: null,
    confirmedAt: null,
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
  });
}
